import { biasRamp, reliabilityFill } from '@/constants/bias'
import { getBiasLabel, getReliabilityLabel } from '@/utils/outlets'

// The one-mark source dot: hue = lean, fill weight = reliability.
//   solid  = reliable       (bg-<hue>)
//   faded  = mixed          (bg-<hue> at half opacity)
//   hollow = below the      (transparent center, <hue> ring) — a shaky source
//            facts floor      recedes without borrowing a second color
// Pass `reliability` to carry both dimensions; omit it for a bias-only dot.
export function BiasDot({ bias, reliability, className = '' }) {
  const { bg, border } = biasRamp(bias ?? 0)
  const hasReliability = reliability != null
  const fill = hasReliability ? reliabilityFill(reliability) : 'solid'

  const weight =
    fill === 'hollow'
      ? `bg-transparent border-[1.5px] ${border}`
      : fill === 'faded'
        ? `${bg} opacity-50`
        : bg

  // Words, not scores (2026-11-01): "Leans Left · Mostly Reliable".
  const relText = hasReliability ? ` · ${getReliabilityLabel(reliability)}` : ''

  return (
    <span
      className={`w-2 h-2 rounded-full box-border flex-shrink-0 ${weight} ${className}`}
      title={`${bias != null ? getBiasLabel(bias) : 'Unrated'}${relText}`}
    />
  )
}

// Combined bias + reliability — now a single dot (position/hue = lean, fill
// weight = reliability) instead of a dot plus a separate green meter.
export function OutletIndicators({ outlet }) {
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <BiasDot bias={outlet?.bias} reliability={outlet?.reliability} />
    </div>
  )
}

// An outlet's lean at row scale: its dot on a left-right line with a center
// tick. Position and hue = lean, fill = reliability (the site's visual
// language; the dot is the same BiasDot the map uses; shared by
// the source directory rows and a source's own page). One outlet has ONE
// position, which is why a dot fits here where stories use the LeanBar.
export function LeanSpot({ outlet, className = 'w-20' }) {
  const x = ((Math.max(-30, Math.min(30, outlet.bias ?? 0)) + 30) / 60) * 90 + 5
  return (
    <span
      className={`relative flex h-2.5 items-center ${className}`}
      aria-hidden="true"
    >
      <span className="h-px w-full bg-muted-foreground/35" />
      <span className="absolute left-1/2 h-2 w-px -translate-x-1/2 bg-muted-foreground/50" />
      <span
        className="absolute flex -translate-x-1/2 rounded-full bg-background p-px"
        style={{ left: `${x}%` }}
      >
        <BiasDot
          bias={outlet.bias}
          reliability={outlet.reliability}
          className="h-2.5 w-2.5"
        />
      </span>
    </span>
  )
}

// Every outlet's round favicon, uploaded by slug.
export const faviconUrl = (slug) =>
  `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${slug}.jpg`
